// src/lib/windgram/scales.js

// Skysight's Ye(): value × unit factor. Verbatim from main bundle.
export function convertUnit(value, unit) {
  switch (unit) {
    case 'ft': return value * 3.28084;
    case 'kts': case 'kt': return value * 1.94384;
    case 'fpm': return value * 196.850394;
    case 'kmh': return value * 3.6;
    case 'm': case 'cm': case 'ms': case 'kph': case 'km': case 'c': return value;
    case 'mph': case 'mi': return value * 0.621371192;
    case 'mh': return value * 2.23694;
    case 'nm': return value * 0.539957;
    case 'f': return value * (9 / 5) + 32;
    default: return value;
  }
}

export const UNIT_LABELS = { m: 'm', ft: 'ft', ms: 'm/s', kts: 'kt', kmh: 'km/h', mph: 'mph', c: '°C', f: '°F' };

const round1 = (x) => Math.round(x * 10) / 10;

// Raw palettes (value, rgb) — verbatim from Skysight main bundle (docs/skysight-windgram-scales.js).
const RAW = {
  ti: [[0,[173,216,230]],[.5,[96,96,180]],[.75,[96,126,213]],[1,[96,157,246]],[1.25,[96,196,228]],[1.5,[97,235,210]],[1.75,[106,220,157]],[2,[116,206,104]],[2.25,[177,225,100]],[2.5,[239,244,97]],[2.75,[242,205,96]],[3,[246,167,96]],[3.25,[240,131,100]],[3.5,[235,96,105]],[3.75,[193,96,139]],[4,[151,96,173]]],
  cldcover: [[0,[173,216,230]],[.4,[96,96,180]],[.6,[96,126,213]],[.8,[96,157,246]],[1,[96,196,228]],[1.2,[97,235,210]],[1.4,[106,220,157]],[1.6,[116,206,104]],[1.8,[177,225,100]],[2,[239,244,97]],[2.2,[242,205,96]],[2.4,[246,167,96]],[2.6,[240,131,100]],[2.8,[235,96,105]],[3,[193,96,139]],[3.2,[151,96,173]]],
  temp: [[-50,[0,0,143]],[-46,[0,0,175]],[-42,[0,0,207]],[-38,[0,0,239]],[-34,[0,15,255]],[-30,[0,47,255]],[-26,[0,79,255]],[-22,[0,111,255]],[-18,[0,143,255]],[-14,[0,175,255]],[-10,[0,207,255]],[-6,[0,239,255]],[-2,[15,255,239]],[2,[47,255,207]],[6,[79,255,175]],[10,[111,255,143]],[14,[143,255,111]],[18,[175,255,79]],[22,[207,255,47]],[26,[239,255,15]],[30,[255,239,0]],[34,[255,207,0]],[38,[255,175,0]],[42,[255,143,0]],[46,[255,111,0]],[50,[255,79,0]]],
  wind: [[0,[241,65,244]],[2,[184,65,244]],[4,[119,66,244]],[6,[0,15,255]],[8,[0,100,255]],[10,[0,185,255]],[12,[0,250,240]],[14,[3,225,160]],[16,[6,200,80]],[18,[23,183,14]],[20,[102,208,9]],[22,[182,233,4]],[24,[255,252,0]],[26,[255,222,0]]],
  lapse: [[-4,[241,65,244]],[-3.5,[184,65,244]],[-3,[119,66,244]],[-2.5,[0,15,255]],[-2,[0,100,255]],[-1.5,[0,185,255]],[-1,[0,250,240]],[-.5,[3,225,160]],[0,[6,200,80]],[.5,[23,183,14]],[1,[102,208,9]],[1.5,[182,233,4]],[2,[255,252,0]],[2.5,[255,222,0]],[3,[255,100,0]]],
  cldfra: [[0,[173,216,230]],[3,[185,185,185]],[9,[178,178,178]],[15,[171,171,171]],[25,[164,164,164]],[35,[157,157,157]],[45,[150,150,150]],[55,[143,143,143]],[67,[140,140,140]],[80,[137,137,137]],[90,[134,134,134]],[100,[130,130,130]]],
  rh: [[0,[241,65,244]],[10,[184,65,244]],[20,[119,66,244]],[30,[0,15,255]],[40,[0,100,255]],[50,[0,185,255]],[60,[0,250,240]],[70,[3,225,160]],[75,[6,200,80]],[80,[23,183,14]],[85,[102,208,9]],[90,[182,233,4]],[95,[255,252,0]],[100,[255,222,0]]],
};

// Which unit each scale's VALUES are converted by (matches the factories' Ye(value, e.*) calls).
const SCALE_UNIT = { ti: 'vert', cldcover: 'vert', temp: 'temp', wind: 'spd', lapse: null, cldfra: null, rh: null };
// ti/cldcover round to 1 dp; temp/wind round to integer; lapse/cldfra/rh not converted.
const SCALE_ROUND = { ti: round1, cldcover: round1, temp: Math.round, wind: Math.round, lapse: null, cldfra: null, rh: null };

// cldfra's FL factory has ONLY an upper sentinel (no lower unshift). All other keys have both.
const HAS_LOWER_SENTINEL = { ti: true, cldcover: true, temp: true, wind: true, lapse: true, cldfra: false, rh: true };

export function makeScale(key, units = {}) {
  const unit = SCALE_UNIT[key];
  const roundFn = SCALE_ROUND[key];
  let bands = RAW[key].map(([value, rgb]) => ({
    value: unit ? roundFn(convertUnit(value, units[unit])) : value,
    rgb,
  }));
  const lowerSentinel = { value: bands[0].value - 1000, rgb: bands[0].rgb };
  const upperSentinel = { value: bands[bands.length - 1].value + 1000, rgb: bands[bands.length - 1].rgb };
  // sentinel below + above (±1000), matching every factory's unshift/push.
  // cldfra has no lower sentinel — matches Skysight FL factory exactly.
  bands = [...(HAS_LOWER_SENTINEL[key] ? [lowerSentinel] : []), ...bands, upperSentinel];
  return bands;
}
